import { useEffect, useState, useCallback } from "react";
import { Tv, RefreshCw, Save, RotateCcw, Eye, Plus, X, Info } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const GROUP_LABEL = { INDEX: "Indices", COMMODITY: "Commodities", CRYPTO: "Crypto" };
const GROUP_COLOR = { INDEX: "amber", COMMODITY: "orange", CRYPTO: "fuchsia" };

export default function FalconViewAdmin() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [customTapeInput, setCustomTapeInput] = useState("");
  const [customHeroInput, setCustomHeroInput] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/falcon-view/config");
      setCfg(r.data);
    } catch (e) {
      toast.error("Failed to load Falcon View config");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const r = await api.put("/falcon-view/config", cfg);
      setCfg(r.data);
      toast.success("Falcon View updated · TV will refresh within 15 seconds");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [cfg]);

  const reset = useCallback(async () => {
    if (!window.confirm("Reset Falcon View to factory defaults?")) return;
    try {
      const r = await api.post("/falcon-view/config/reset");
      setCfg(r.data);
      toast.success("Reset to defaults");
    } catch (e) { toast.error("Reset failed"); }
  }, []);

  const toggleRailItem = (symbol) => {
    setCfg((c) => ({
      ...c,
      side_rail_items: (c.side_rail_items || []).map((r) => r.symbol === symbol ? { ...r, enabled: !(r.enabled !== false) } : r),
    }));
  };

  const removeRailItem = (symbol) => {
    setCfg((c) => ({ ...c, side_rail_items: (c.side_rail_items || []).filter((r) => r.symbol !== symbol) }));
  };

  const addTapeSymbol = () => {
    const raw = (customTapeInput || "").trim().toUpperCase();
    if (!raw) return;
    setCfg((c) => ({
      ...c,
      ticker_tape_source: "custom",
      ticker_tape_custom: [...(c.ticker_tape_custom || []), raw],
    }));
    setCustomTapeInput("");
  };

  const removeTapeSymbol = (sym) => {
    setCfg((c) => ({ ...c, ticker_tape_custom: (c.ticker_tape_custom || []).filter((s) => s !== sym) }));
  };

  const addHeroSymbol = () => {
    const raw = (customHeroInput || "").trim().toUpperCase();
    if (!raw) return;
    setCfg((c) => ({
      ...c,
      hero_chart_source: "custom",
      hero_chart_custom: [...(c.hero_chart_custom || []), raw],
    }));
    setCustomHeroInput("");
  };

  const removeHeroSymbol = (sym) => {
    setCfg((c) => ({ ...c, hero_chart_custom: (c.hero_chart_custom || []).filter((s) => s !== sym) }));
  };

  if (!cfg) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 pb-24" data-testid="falcon-admin-root">
      {/* Header */}
      <header className="flex items-center justify-between gap-4 rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent p-5">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 via-orange-500 to-red-600 shadow-lg shadow-amber-500/40">
            <Tv className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-white">
              FALCON <span className="text-amber-400">VIEW</span>
              <span className="ml-3 rounded-full bg-amber-500/20 px-2 py-0.5 align-middle text-[10px] font-bold uppercase tracking-widest text-amber-300">Admin Console</span>
            </h1>
            <p className="mt-0.5 text-sm text-slate-400">Configure what shows on the TV · Powered by A V Tech</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to="/tv"
            target="_blank"
            className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-300 hover:border-amber-500 hover:text-amber-300"
            data-testid="falcon-admin-preview-btn"
          >
            <Eye className="h-3.5 w-3.5" /> Preview TV
          </Link>
          <button
            onClick={reset}
            className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-400 hover:border-red-500 hover:text-red-300"
            data-testid="falcon-admin-reset-btn"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
          <button
            onClick={load}
            className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-400 hover:border-slate-500 hover:text-white"
            data-testid="falcon-admin-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Reload
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 text-xs font-black uppercase tracking-widest text-black shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 disabled:opacity-60"
            data-testid="falcon-admin-save-btn"
          >
            <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save & Push"}
          </button>
        </div>
      </header>

      {/* How it works */}
      <div className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-300">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
        <div>
          <p className="font-bold text-white">How Falcon View works</p>
          <p className="mt-1 text-slate-400">
            The TV device logs in with PIN <code className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-amber-300">7869</code> and shows only this display.
            Changes you make here appear on the TV within 15 seconds. You (admin PIN <code className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-emerald-300">83528</code>) keep full app access.
          </p>
        </div>
      </div>

      {/* Branding */}
      <section className="rounded-xl border border-slate-800 bg-slate-950/60 p-5">
        <h2 className="mb-3 text-xs font-black uppercase tracking-[0.24em] text-slate-400">Branding</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-500">Title</label>
            <input
              type="text"
              value={cfg.brand_title || ""}
              onChange={(e) => setCfg({ ...cfg, brand_title: e.target.value })}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 font-mono text-lg font-bold text-white focus:border-amber-500 focus:outline-none"
              data-testid="falcon-admin-title-input"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-500">Subtitle</label>
            <input
              type="text"
              value={cfg.brand_subtitle || ""}
              onChange={(e) => setCfg({ ...cfg, brand_subtitle: e.target.value })}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
              data-testid="falcon-admin-subtitle-input"
            />
          </div>
        </div>
      </section>

      {/* Hero Chart */}
      <section className="rounded-xl border border-slate-800 bg-slate-950/60 p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Hero Chart (main center)</h2>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={cfg.hero_chart_enabled !== false}
              onChange={(e) => setCfg({ ...cfg, hero_chart_enabled: e.target.checked })}
              className="h-4 w-4 accent-amber-500"
              data-testid="falcon-admin-hero-enabled"
            />
            Enabled
          </label>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-500">Source</label>
            <select
              value={cfg.hero_chart_source || "auto"}
              onChange={(e) => setCfg({ ...cfg, hero_chart_source: e.target.value })}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
              data-testid="falcon-admin-hero-source"
            >
              <option value="auto">Auto (your active picks)</option>
              <option value="custom">Custom list</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Rotate every {cfg.hero_chart_rotate_seconds || 30}s
            </label>
            <input
              type="range" min="5" max="120" step="5"
              value={cfg.hero_chart_rotate_seconds || 30}
              onChange={(e) => setCfg({ ...cfg, hero_chart_rotate_seconds: parseInt(e.target.value, 10) })}
              className="w-full accent-amber-500"
              data-testid="falcon-admin-hero-rotate"
            />
          </div>
          <div className="text-[11px] text-slate-500">
            {cfg.hero_chart_source === "auto"
              ? "Cycles through your Emergent Promise + Synthesis picks + portfolio automatically."
              : "Cycles through the custom symbol list below."}
          </div>
        </div>
        {cfg.hero_chart_source === "custom" && (
          <div className="mt-4">
            <div className="mb-2 flex gap-2">
              <input
                type="text" placeholder="e.g. AAPL or NASDAQ:AAPL"
                value={customHeroInput}
                onChange={(e) => setCustomHeroInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addHeroSymbol()}
                className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 font-mono text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="falcon-admin-hero-input"
              />
              <button onClick={addHeroSymbol} className="flex items-center gap-1 rounded-lg bg-amber-500/20 px-3 py-1.5 text-xs font-bold uppercase text-amber-300 hover:bg-amber-500/30">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(cfg.hero_chart_custom || []).map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 font-mono text-xs font-bold text-amber-300">
                  {s}
                  <button onClick={() => removeHeroSymbol(s)} className="text-amber-400 hover:text-red-400"><X className="h-3 w-3" /></button>
                </span>
              ))}
              {(cfg.hero_chart_custom || []).length === 0 && <span className="text-xs text-slate-500">No custom symbols yet</span>}
            </div>
          </div>
        )}
      </section>

      {/* Side Rail */}
      <section className="rounded-xl border border-slate-800 bg-slate-950/60 p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">CNBC-style Side Rail</h2>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={cfg.side_rail_enabled !== false}
              onChange={(e) => setCfg({ ...cfg, side_rail_enabled: e.target.checked })}
              className="h-4 w-4 accent-amber-500"
              data-testid="falcon-admin-rail-enabled"
            />
            Enabled
          </label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {["INDEX", "COMMODITY", "CRYPTO"].map((group) => (
            <div key={group} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
              <div className={`mb-2 text-[10px] font-black uppercase tracking-widest text-${GROUP_COLOR[group]}-300`}>
                {GROUP_LABEL[group]}
              </div>
              <div className="space-y-1.5">
                {(cfg.side_rail_items || []).filter((r) => r.group === group).map((r) => (
                  <div key={r.symbol} className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-950/60 px-2.5 py-1.5">
                    <label className="flex flex-1 items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={r.enabled !== false}
                        onChange={() => toggleRailItem(r.symbol)}
                        className="h-3.5 w-3.5 accent-amber-500"
                        data-testid={`falcon-admin-rail-toggle-${r.symbol}`}
                      />
                      <div>
                        <div className="text-sm font-bold text-white">{r.name}</div>
                        <div className="font-mono text-[10px] text-slate-500">{r.symbol}</div>
                      </div>
                    </label>
                    <button onClick={() => removeRailItem(r.symbol)} className="text-slate-600 hover:text-red-400" title="Remove">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {(cfg.side_rail_items || []).filter((r) => r.group === group).length === 0 && (
                  <div className="text-[10px] italic text-slate-600">None</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Ticker Tape */}
      <section className="rounded-xl border border-slate-800 bg-slate-950/60 p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Bottom Ticker Tape</h2>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={cfg.ticker_tape_enabled !== false}
              onChange={(e) => setCfg({ ...cfg, ticker_tape_enabled: e.target.checked })}
              className="h-4 w-4 accent-amber-500"
              data-testid="falcon-admin-tape-enabled"
            />
            Enabled
          </label>
        </div>
        <div className="mb-3">
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-500">Source</label>
          <select
            value={cfg.ticker_tape_source || "auto"}
            onChange={(e) => setCfg({ ...cfg, ticker_tape_source: e.target.value })}
            className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
            data-testid="falcon-admin-tape-source"
          >
            <option value="auto">Auto (your picks + portfolio + market anchors)</option>
            <option value="custom">Custom list (only symbols you add below)</option>
          </select>
        </div>
        {cfg.ticker_tape_source === "custom" && (
          <div>
            <div className="mb-2 flex gap-2">
              <input
                type="text" placeholder="e.g. AAPL or COINBASE:BTCUSD"
                value={customTapeInput}
                onChange={(e) => setCustomTapeInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTapeSymbol()}
                className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 font-mono text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="falcon-admin-tape-input"
              />
              <button onClick={addTapeSymbol} className="flex items-center gap-1 rounded-lg bg-amber-500/20 px-3 py-1.5 text-xs font-bold uppercase text-amber-300 hover:bg-amber-500/30">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(cfg.ticker_tape_custom || []).map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 font-mono text-xs font-bold text-amber-300">
                  {s}
                  <button onClick={() => removeTapeSymbol(s)} className="text-amber-400 hover:text-red-400"><X className="h-3 w-3" /></button>
                </span>
              ))}
              {(cfg.ticker_tape_custom || []).length === 0 && <span className="text-xs text-slate-500">No custom symbols yet</span>}
            </div>
          </div>
        )}
      </section>

      {/* Coming Soon Slides */}
      <section className="rounded-xl border border-dashed border-slate-800 bg-slate-950/30 p-5">
        <h2 className="mb-2 text-xs font-black uppercase tracking-[0.24em] text-slate-500">Slide Modules (coming next)</h2>
        <p className="text-sm text-slate-500">
          You mentioned this is just the beginning. Send the next display task and I&apos;ll add it here as a togglable slide module (e.g. Emergent Promise strip, Whale Sweeps ticker, News crawler, Trump Tracker, etc).
        </p>
      </section>
    </div>
  );
}
